import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { useAuthStore } from '../../app/store/auth-store';
import { LanguageSwitcher } from '../../shared/ui/language-switcher';
import {
  LayoutDashboard,
  Users,
  Shield,
  LogOut,
  User,
  Menu,
} from 'lucide-react';

interface NavigationItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
}

const navigationItems: NavigationItem[] = [
  {
    title: 'navigation.dashboard',
    url: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'navigation.users',
    url: '/users',
    icon: Users,
    permission: 'users:read',
  },
  {
    title: 'navigation.roles',
    url: '/roles',
    icon: Shield,
    permission: 'roles:read',
  },
];

interface MobileNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileNav: React.FC<MobileNavProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuthStore();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
      onOpenChange(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigate = (url: string) => {
    navigate(url);
    onOpenChange(false);
  };

  const filteredNavigationItems = navigationItems.filter(item => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle navigation menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
        <SheetHeader className="border-b p-4">
          <SheetTitle className="flex items-center gap-2 text-left">
            <Shield className="h-6 w-6 text-primary" />
            Admin Panel
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Navigation Items */}
          <div className="space-y-2">
            {filteredNavigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.url;
              
              return (
                <Button
                  key={item.url}
                  variant={isActive ? "default" : "ghost"}
                  onClick={() => handleNavigate(item.url)}
                  className="w-full justify-start gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {t(item.title)}
                </Button>
              );
            })}
          </div>

          <Separator />

          {/* User Info */}
          {user && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground">
                <User className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user.email
                  }
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {user.email}
                </p>
              </div>
            </div>
          )}

        </div>
        
        {/* Footer with logout */}
        <div className="border-t p-4 space-y-3">
          {/* Language Switcher */}
          <div className="flex justify-center">
            <LanguageSwitcher showText />
          </div>

          {/* Logout Button */}
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full justify-start gap-2"
          >
            <LogOut className="h-4 w-4" />
            {t('auth.logout')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};